import { OrganizationZapFolderId } from "@zapier/bff";
import { ZAP_PAGE_SIZE } from "@zapier/utils";
import { useHydrateStorageZaps } from "./useHydrateStorageZaps";
import { useOrganizationZapsPagination } from "./useOrganizationZapsPagination";
import { useQueryOrganizationZaps } from "./useQueryOrganizationZaps";
import { StorageZap } from "./useQueryStorageZaps";

interface Input {
  enabled?: boolean;
  folderId?: OrganizationZapFolderId;
  ownerId?: number;
  page?: number;
  pageSize?: number;
  subfoldersOffset?: number; // allow the first page of Zaps to be shorter depending on subfolder length
}

export function useOrganizationZaps({
  enabled = true,
  folderId,
  ownerId,
  page = 1,
  pageSize = ZAP_PAGE_SIZE,
  subfoldersOffset = 0,
}: Input) {
  const { limit, offset } = useOrganizationZapsPagination({
    page,
    pageSize,
    subfoldersOffset,
  });

  const {
    errorStatus,
    hideZaps,
    isError,
    isFetching,
    isLoading,
    response: zapsResponse,
  } = useQueryOrganizationZaps({
    enabled,
    folderId,
    limit,
    offset,
    ownerId,
  });

  const storageZaps =
    (zapsResponse?.results as StorageZap[]) || ([] as StorageZap[]);

  const { list: hydratedZaps } = useHydrateStorageZaps(storageZaps);

  const organizationZaps = {
    zaps: hydratedZaps,
    meta: zapsResponse?.meta || { count: 0, offset: 0, limit: 0 },
  };

  const totalResults = organizationZaps.meta.count + subfoldersOffset;
  const totalPages = Math.ceil(totalResults / pageSize) || 1;

  return {
    errorStatus,
    isError,
    isFetching,
    isLoading,
    meta: organizationZaps.meta,
    totalPages,
    totalCount: organizationZaps.meta.count,
    totalResults,
    zaps: hideZaps ? [] : organizationZaps.zaps,
  };
}
